import { useEffect, useRef, useState } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { useBranding } from '../../hooks/useBranding';
import { Upload, Trash2, Image as ImageIcon } from 'lucide-react';

// Small reusable asset uploader: preview + choose-file (auto-upload) + remove.
function AssetUploader({ label, hint, currentUrl, onUpload, onRemove, square }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const choose = () => fileRef.current?.click();

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file
    if (!file) return;
    setBusy(true);
    try { await onUpload(file); } finally { setBusy(false); }
  };

  return (
    <div className="brand-asset">
      <div className={`brand-asset-preview${square ? ' square' : ''}`}>
        {currentUrl
          ? <img src={currentUrl} alt={label} />
          : <span className="brand-asset-empty"><ImageIcon size={20} /> No image</span>}
      </div>
      <div className="brand-asset-body">
        <strong>{label}</strong>
        {hint && <p className="form-hint">{hint}</p>}
        <div className="brand-asset-actions">
          <button className="btn btn-secondary btn-sm" onClick={choose} disabled={busy}>
            <Upload size={14} /> {busy ? 'Uploading…' : (currentUrl ? 'Replace' : 'Upload')}
          </button>
          {currentUrl && (
            <button className="btn btn-ghost btn-sm" onClick={onRemove} disabled={busy}>
              <Trash2 size={14} /> Remove
            </button>
          )}
        </div>
        <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml"
          style={{ display: 'none' }} onChange={onPick} />
      </div>
    </div>
  );
}

export default function BrandingAdminPage() {
  const { refresh: refreshGlobalBranding } = useBranding();
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingSettings, setSavingSettings] = useState(false);

  // local copies of editable settings
  const [bgEnabled, setBgEnabled] = useState(false);
  const [bgPlacement, setBgPlacement] = useState('toolbars');
  const [footerCompany, setFooterCompany] = useState('');

  const load = () =>
    api.get('/api/branding/admin/state').then(r => {
      setState(r.data);
      setBgEnabled(!!r.data.background?.enabled);
      setBgPlacement(r.data.background?.placement || 'toolbars');
      setFooterCompany(r.data.footerCompany || '');
      setLoading(false);
    });

  useEffect(() => { load(); }, []);

  const afterChange = async () => { await load(); refreshGlobalBranding(); };

  // ── asset handlers ──
  const uploadTo = (url) => async (file) => {
    const fd = new FormData();
    fd.append('file', file);
    try {
      await api.post(url, fd);
      toast.success('Uploaded');
      await afterChange();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Upload failed');
    }
  };
  const removeAt = (url, label) => async () => {
    if (!window.confirm(`Remove ${label}?`)) return;
    try {
      await api.delete(url);
      toast.success('Removed');
      await afterChange();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Remove failed');
    }
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      await api.put('/api/branding/settings', {
        bg_enabled: bgEnabled,
        bg_placement: bgPlacement,
        footer_company: footerCompany,
      });
      toast.success('Settings saved');
      await afterChange();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Save failed');
    } finally {
      setSavingSettings(false);
    }
  };

  return (
    <>
      <div className="topbar"><h2>Branding</h2></div>
      <div className="page">
        {loading || !state ? <div className="loading">Loading…</div> : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20, maxWidth: 860 }}>

            {/* Toledo Ticket logo */}
            <div className="card">
              <div className="card-header"><h3>Toledo Ticket Logo (sidebar)</h3></div>
              <AssetUploader
                label="Square logo shown at the top of the left navigation"
                hint="Best results: SVG, or a transparent PNG at 512×512. Displayed up to ~180px square."
                currentUrl={state.ttcLogo}
                square
                onUpload={uploadTo('/api/branding/ttc-logo')}
                onRemove={removeAt('/api/branding/ttc-logo', 'the Toledo Ticket logo')}
              />
            </div>

            <div className="card">
              <div className="card-header"><h3>Default Product Image</h3></div>
              <AssetUploader
                label="Fallback graphic shown for any item without its own image"
                hint="Used on the catalog, cart, and order screens. If not set, a built-in paper-ticket graphic is used. Best: SVG or PNG, roughly 4:3."
                currentUrl={state.defaultProductImage}
                onUpload={uploadTo('/api/branding/default-product-image')}
                onRemove={removeAt('/api/branding/default-product-image', 'the default product image')}
              />
            </div>

            {/* Customer logos */}
            <div className="card">
              <div className="card-header"><h3>Customer Logos (top bar)</h3></div>
              <p className="form-hint" style={{ marginBottom: 16 }}>
                Shown in the top-left of the page for that customer's users. Best results: SVG or
                transparent PNG, sized for ~36–40px height (e.g. 400×100).
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {state.customers.map(c => (
                  <div key={c.id}>
                    <AssetUploader
                      label={c.name}
                      currentUrl={state.customerLogos[c.id]}
                      onUpload={uploadTo(`/api/branding/customer-logo/${c.id}`)}
                      onRemove={removeAt(`/api/branding/customer-logo/${c.id}`, `${c.name}'s logo`)}
                    />
                  </div>
                ))}
                {state.customers.length === 0 && <p className="text-muted">No customers yet.</p>}
              </div>
            </div>

            {/* Background tile */}
            <div className="card">
              <div className="card-header"><h3>Background Tile</h3></div>
              <AssetUploader
                label="Seamless tile image"
                hint="Use a seamlessly tileable image (300–600px square). Keep it light/low-contrast so text stays readable."
                currentUrl={state.background.url}
                onUpload={uploadTo('/api/branding/background')}
                onRemove={removeAt('/api/branding/background', 'the background tile')}
              />
              <div className="form-row" style={{ marginTop: 16 }}>
                <div className="form-group">
                  <label className="form-label">Enable background</label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14 }}>
                    <input type="checkbox" checked={bgEnabled} onChange={e => setBgEnabled(e.target.checked)} />
                    Show the tile {state.background.url ? '' : '(upload a tile first)'}
                  </label>
                </div>
                <div className="form-group">
                  <label className="form-label">Placement</label>
                  <select className="form-select" value={bgPlacement} onChange={e => setBgPlacement(e.target.value)}>
                    <option value="toolbars">Sidebar &amp; top bar</option>
                    <option value="page">Behind page content</option>
                    <option value="both">Both</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="card">
              <div className="card-header"><h3>Footer</h3></div>
              <div className="form-group">
                <label className="form-label">Company name (copyright)</label>
                <input className="form-input" value={footerCompany}
                  onChange={e => setFooterCompany(e.target.value)}
                  placeholder="Toledo Ticket Technologies" />
                <p className="form-hint">Shown as “© {new Date().getFullYear()} {footerCompany || 'Toledo Ticket Technologies'}. All rights reserved.” The version number is read automatically from the app.</p>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button className="btn btn-primary" onClick={saveSettings} disabled={savingSettings}>
                {savingSettings ? 'Saving…' : 'Save settings'}
              </button>
            </div>
            <p className="form-hint">Logos and the tile upload immediately. The button above saves the background toggle/placement and footer name.</p>
          </div>
        )}
      </div>
    </>
  );
}
